/**
 * Browser-timezone, display-only date/time helpers. Outputs strings for UI labels only;
 * do not use for API payloads, sort keys, or filter state values.
 */

export const EPOCH_DISPLAY_FALLBACK = '\u2014'

export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Zone / IANA disambiguation for tooltips — display-only, not a payload. */
export function buildTimeZoneTitle(date: Date, timeZone: string): string {
  const shortF = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'short' })
  const longF = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: 'longGeneric' })
  const shortName = shortF.formatToParts(date).find((p) => p.type === 'timeZoneName')?.value
  const longName = longF.formatToParts(date).find((p) => p.type === 'timeZoneName')?.value
  const parts = [longName, shortName, timeZone].filter(
    (x): x is string => typeof x === 'string' && x.length > 0
  )
  return [...new Set(parts)].join(' \u00b7 ')
}

export interface EpochDisplayOptions {
  /** Default true: two-line time + date when compact */
  compact?: boolean
  timeZone?: string
  /** When false, omit tooltip line (e.g. user disabled title) */
  showTimezoneTooltip?: boolean
  /** Include seconds in the formatted time (single-line and compact time part). */
  includeSeconds?: boolean
  /** Date portion only in the main label (no time). */
  dateOnly?: boolean
  /** 12h vs 24h for time portions (default true to match `DateTimeDisplay` tables). */
  hour12?: boolean
}

export interface EpochDisplayResult {
  primaryLabel: string
  /** Two-line layout for compact table cells */
  compactLines?: { time: string; date: string }
  /** Zone text for `title` / aria — not appended to main label */
  tooltipLabel?: string
}

export function isValidEpochMicros(v: number | null | undefined): v is number {
  return typeof v === 'number' && !Number.isNaN(v) && v > 0
}

/**
 * Normalize API timestamps (seconds, milliseconds, or microseconds) to epoch microseconds
 * for {@link EpochMicrosLabel} / {@link formatEpochMicrosForDisplay}.
 */
export function normalizeToEpochMicros(ts: number | null | undefined): number | null {
  if (ts == null || ts === 0) return null
  if (ts >= 1e15) return ts
  if (ts >= 1e12) return ts * 1000
  if (ts >= 1e9) return ts * 1_000_000
  return ts * 1000
}

export function formatEpochMicrosForDisplay(
  epochMicros: number | null | undefined,
  options?: EpochDisplayOptions
): EpochDisplayResult {
  const compact = options?.compact !== false
  const tz = options?.timeZone ?? getBrowserTimeZone()
  const showTip = options?.showTimezoneTooltip !== false
  const includeSeconds = options?.includeSeconds === true
  const dateOnly = options?.dateOnly === true
  const hour12 = options?.hour12 !== false

  if (!isValidEpochMicros(epochMicros)) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }

  const em = epochMicros
  const date = new Date(em / 1000)
  if (Number.isNaN(date.getTime())) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }

  const timeFieldOpts: Intl.DateTimeFormatOptions = {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12,
    ...(includeSeconds ? { second: '2-digit' as const } : {}),
  }
  const timePart = new Intl.DateTimeFormat(undefined, timeFieldOpts).format(date)

  const datePart = new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)

  const oneLine = new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12,
    ...(includeSeconds ? { second: '2-digit' as const } : {}),
  }).format(date)

  const oneLineDateOnly = new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)

  const tooltipLabel = showTip ? buildTimeZoneTitle(date, tz) : undefined

  if (dateOnly) {
    return { primaryLabel: oneLineDateOnly, tooltipLabel }
  }

  if (compact) {
    return {
      primaryLabel: oneLine,
      compactLines: { time: timePart, date: datePart },
      tooltipLabel,
    }
  }

  return { primaryLabel: oneLine, tooltipLabel }
}

/** ISO-8601 string (API / DB) to display result — display-only, never for payloads. */
export function formatIsoDateTimeForDisplay(
  iso: string | null | undefined,
  options?: EpochDisplayOptions
): EpochDisplayResult {
  if (iso == null || String(iso).trim() === '') {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }
  return formatEpochMicrosForDisplay(d.getTime() * 1000, options)
}

/**
 * Time-of-day in local (browser) zone, for dense charts/axes. HH:mm:ss, 24h.
 */
export function formatEpochTimeHmsForDisplay(
  epochMicros: number | null | undefined,
  options?: { timeZone?: string; showTimezoneTooltip?: boolean; includeSeconds?: boolean }
): Pick<EpochDisplayResult, 'primaryLabel' | 'tooltipLabel'> {
  const tz = options?.timeZone ?? getBrowserTimeZone()
  const showTip = options?.showTimezoneTooltip !== false
  const includeSeconds = options?.includeSeconds !== false

  if (!isValidEpochMicros(epochMicros)) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }
  const date = new Date(epochMicros! / 1000)
  if (Number.isNaN(date.getTime())) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }

  const primaryLabel = new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds ? { second: '2-digit' as const } : {}),
    hour12: false,
  }).format(date)
  return {
    primaryLabel,
    tooltipLabel: showTip ? buildTimeZoneTitle(date, tz) : undefined,
  }
}

/**
 * Terminal-style: `HH:MM:SS.mmm` in the given zone + zone tooltip.
 */
export function formatEpochLogLineForDisplay(
  epochMicros: number | null | undefined,
  options?: { timeZone?: string; showTimezoneTooltip?: boolean }
): Pick<EpochDisplayResult, 'primaryLabel' | 'tooltipLabel'> {
  const tz = options?.timeZone ?? getBrowserTimeZone()
  const showTip = options?.showTimezoneTooltip !== false
  if (!isValidEpochMicros(epochMicros)) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }
  const date = new Date(epochMicros! / 1000)
  if (Number.isNaN(date.getTime())) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }
  const timeNoMs = new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date)
  const primaryLabel = `${timeNoMs}.${String(date.getMilliseconds()).padStart(3, '0')}`
  return { primaryLabel, tooltipLabel: showTip ? buildTimeZoneTitle(date, tz) : undefined }
}

export function formatIsoDateForDisplay(
  isoString: string,
  options?: { timeZone?: string }
): string {
  const d = new Date(isoString)
  if (Number.isNaN(d.getTime())) {
    return isoString
  }
  const tz = options?.timeZone ?? getBrowserTimeZone()
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d)
}

export interface RelativeDisplayOptions {
  timeZone?: string
  /** For tests */
  now?: number
  showTooltip?: boolean
}

/** Relative primary label + rich tooltip (local + zone) for `title` */
export function formatRelativeEpochForDisplay(
  epochMicros: number | null | undefined,
  options?: RelativeDisplayOptions
): Pick<EpochDisplayResult, 'primaryLabel' | 'tooltipLabel'> {
  const tz = options?.timeZone ?? getBrowserTimeZone()
  const now = options?.now ?? Date.now()
  const showTip = options?.showTooltip !== false

  if (!isValidEpochMicros(epochMicros)) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }

  const em = epochMicros
  const date = new Date(em / 1000)
  if (Number.isNaN(date.getTime())) {
    return { primaryLabel: EPOCH_DISPLAY_FALLBACK }
  }

  const diffMs = date.getTime() - now
  const s = diffMs / 1000
  const absS = Math.abs(s)
  
  // Use the language selected by the user, fallback to undefined (browser default)
  const lang = window.localStorage.getItem('i18nextLng') || undefined
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })
  
  let label: string
  if (absS < 60) {
    label = rtf.format(Math.trunc(s), 'second')
  } else if (absS < 3600) {
    label = rtf.format(Math.trunc(s / 60), 'minute')
  } else if (absS < 86400) {
    label = rtf.format(Math.trunc(s / 3600), 'hour')
  } else if (absS < 604800) {
    label = rtf.format(Math.trunc(s / 86400), 'day')
  } else if (absS < 2592000) {
    label = rtf.format(Math.trunc(s / 604800), 'week')
  } else if (absS < 31557600) {
    label = rtf.format(Math.trunc(s / 2592000), 'month')
  } else {
    label = rtf.format(Math.trunc(s / 31557600), 'year')
  }

  if (!showTip) {
    return { primaryLabel: label }
  }

  const localFull = new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
  const z = buildTimeZoneTitle(date, tz)
  return { primaryLabel: label, tooltipLabel: `${localFull} \u00b7 ${z}` }
}

/**
 * Coerce a cell value to Date using the same unit heuristics as DataTable export.
 * Display-only: for CSV/clipboard string labels, not for API.
 */
function coerceToDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'number') {
    if (value > 1e14) {
      return new Date(value / 1000)
    }
    if (value > 1e11) {
      return new Date(value)
    }
    return new Date(value * 1000)
  }
  if (typeof value === 'string' && value.length > 0) {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

/** Single-line label for table export; row objects are never mutated */
export function formatTimestampValueForExport(value: unknown, timeZone?: string): string {
  if (value == null) {
    return ''
  }
  const d = coerceToDate(value)
  if (!d) {
    return String(value)
  }
  const tz = timeZone ?? getBrowserTimeZone()
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d)
}
